import { animated, useSpring } from 'react-spring';
import { formatDate } from '../../GlobalFunctions';
import { generateBarChart } from '../../ClassroomDashboard/PeekavilleDataHelpers';
import { EmptyBlock, NavArrowLeft, NavArrowRight } from './NavArrow';
import { Icon } from '@iconify/react';
import faceHappy from '@iconify/icons-akar-icons/face-happy';
import faceNeutral from '@iconify/icons-akar-icons/face-neutral';
import faceSad from '@iconify/icons-akar-icons/face-sad';
import styles from './ChartSegment.module.scss';

interface Props {
  apiConfig: object;
  barChartData: unknown;
  currentSelectedDay: Date;
  dateSpan: Date[];
  goToNextDateSet: () => void;
  goToPreviousDateSet: () => void;
  showNextWeekNav: boolean;
  showPreviousWeekNav: boolean;
}

const ChartSegment = ({
  apiConfig,
  barChartData,
  currentSelectedDay,
  dateSpan,
  goToNextDateSet,
  goToPreviousDateSet,
  showNextWeekNav,
  showPreviousWeekNav,
}: Props): JSX.Element => {
  const [props, api] = useSpring(
    () => ({
      config: { mass: 0.3, tension: 300, friction: 10, clamp: true },
      from: { transform: 'translate3d(0%, 0, 0)', opacity: 1 },
    }),
    [],
  );

  const handleAnimation = () => {
    api.start(apiConfig);
  };

  const getWeekdayFirstLetter = (day: Date) => {
    const weekday = day.getDay();
    return weekday === 4 ? 'R' : day.toLocaleDateString('en-us', { weekday: 'narrow' });
  };

  const goToPrevious = () => {
    handleAnimation();
    goToPreviousDateSet();
  };

  const goToNext = () => {
    handleAnimation();
    goToNextDateSet();
  };

  return (
    <section className={styles.chartSegment}>
      <div className={styles.title}>Weekly Overview</div>
      <h4>Week of {formatDate(dateSpan[0])}</h4>
      <div className={styles.barChartContainer}>
        <div className={styles.chartBase}>
          <div className={styles.happyRow}>
            <Icon className={styles.happyFaceIcon} icon={faceHappy} />
            <hr />
          </div>
          <div className={styles.neutralRow}>
            <Icon className={styles.neutralFaceIcon} icon={faceNeutral} />
            <hr />
          </div>
          <div className={styles.sadRow}>
            <Icon className={styles.sadFaceIcon} icon={faceSad} />
            <hr />
          </div>
          <div className={styles.elementsRow}>
            <EmptyBlock />
            <div className={styles.elements}>{generateBarChart(barChartData)}</div>
            <EmptyBlock />
          </div>
        </div>
        <div className={styles.weekRow}>
          {showPreviousWeekNav ? <NavArrowLeft goToPrevious={goToPrevious} /> : <EmptyBlock />}
          <div className={styles.hideOverflow}>
            <animated.div className={styles.days} style={props}>
              {dateSpan.map((day: Date, index) => {
                return (
                  <div key={`${day}${index}`} className={styles.titleCell}>
                    <h4 className={formatDate(currentSelectedDay) === formatDate(day) ? styles.highLight : undefined}>
                      {getWeekdayFirstLetter(day)}
                    </h4>
                  </div>
                );
              })}
            </animated.div>
          </div>
          {showNextWeekNav ? <NavArrowRight goToNext={goToNext} /> : <EmptyBlock />}
        </div>
      </div>
    </section>
  );
};

export default ChartSegment;
